import React, { useState, useEffect } from "react";
import { InputForm } from "../../_helpers/components/Forms/InputLabel";
import { Button } from "../../_helpers/components/Button";
import toast from "react-hot-toast";
import { registerUser } from "../../_api/User";
import { useNavigate } from "react-router-dom";
import versailles from "../../../../../public/imgs/versailles/background.jpg";
import { getGroupsBySubdomain } from "../../_api/Company";

export const Inscription = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [lastName, setLastName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [password, setPassword] = useState("");
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const subdomain = window.location.hostname.split(".")[0];
    const isEpvSubdomain = subdomain === "epv";

    const validatePassword = (pwd) => {
        const minLength = isEpvSubdomain ? 12 : 6;
        const lengthCheck = pwd.length >= minLength;

        if (!isEpvSubdomain) {
            return {
                lengthCheck,
                isValid: lengthCheck,
            };
        }

        const specialCharCheck = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
        const uppercaseCheck = /[A-Z]/.test(pwd);
        const lowercaseCheck = /[a-z]/.test(pwd);
        const digitCheck = /[0-9]/.test(pwd);

        return {
            lengthCheck,
            specialCharCheck,
            uppercaseCheck,
            lowercaseCheck,
            digitCheck,
            isValid:
                lengthCheck &&
                specialCharCheck &&
                uppercaseCheck &&
                lowercaseCheck &&
                digitCheck,
        };
    };

    useEffect(() => {
        getGroupsBySubdomain(subdomain)
            .then((resp) => setGroups(resp.data))
            .catch(() => setGroups([])); // handle 404 ou autres erreurs
    }, []);

    const register = () => {
        if (email === "") {
            toast.error("Il faut remplir un email.");
            return;
        }
        if (lastName === "") {
            toast.error("Il faut remplir votre nom.");
            return;
        }
        if (firstName === "") {
            toast.error("Il faut remplir votre prénom.");
            return;
        }

        const validation = validatePassword(password);

        if (!validation.isValid) {
            setError("Le mot de passe ne remplit pas tous les critères.");
            return;
        }

        if (groups.length > 0 && !selectedGroup) {
            toast.error("Veuillez sélectionner un groupe.");
            return;
        }

        setError(null);

        setIsSubmitting(true);
        const payload = {
            email: email,
            group_id: selectedGroup,
            first_name: firstName,
            last_name: lastName,
            company: subdomain,
            password: password,
        };

        registerUser(payload)
            .then((resp) => {
                toast(resp.data.message);
                setEmail("");
                setLastName("");
                setFirstName("");
                setPassword("");
                navigate("/login");
            })
            .catch((resp) => {
                toast.error(resp.response.data.message);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const validation = validatePassword(password);

    return (
        <div className="w-full lg:w-1/2 flex justify-center items-center">
            <div className="bg-white p-4 rounded-lg shadow-md w-3/4 max-w-md">
                <div className="lg:hidden w-full h-48 mb-4">
                    <img
                        src={versailles}
                        alt="Logo"
                        onClick={() => navigate("/")}
                        className="w-full h-full object-cover cursor-pointer"
                    />
                </div>
                <h4 className="text-center font-bold text-base">
                    Bienvenue
                </h4>
                <p className="text-center italic text-xs mb-4">
                    Enregistrer vos informations pour créer votre compte.
                </p>
                <div className="space-y-4">
                    <InputForm
                        type="mail"
                        name="Mail"
                        value={email}
                        set={setEmail}
                        placeholder={"prenom.nom@domaine.fr"}
                        required={true}
                    />
                    {groups.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Groupe de rattachement
                            </label>
                            <select
                                value={selectedGroup ?? ""}
                                onChange={(e) =>
                                    setSelectedGroup(e.target.value)
                                }
                                className="border rounded-sm px-3 py-2 w-full text-sm"
                            >
                                <option value="">
                                    -- Choisir un groupe --
                                </option>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <InputForm
                        name="Nom"
                        value={lastName}
                        set={setLastName}
                        placeholder={"Nom"}
                        required={true}
                    />
                    <InputForm
                        name="Prénom"
                        value={firstName}
                        set={setFirstName}
                        placeholder={"Prénom"}
                        required={true}
                    />
                    <InputForm
                        name="Mot de passe"
                        value={password}
                        set={setPassword}
                        type={"password"}
                        placeholder={"**********"}
                        required={true}
                    />

                    {/* Critères de mot de passe */}
                    <div className="text-xs text-left mt-1 space-y-1">
                        <p
                            className={
                                validation.lengthCheck
                                    ? "text-green-600"
                                    : "text-red-600"
                            }
                        >
                            • Au moins {isEpvSubdomain ? 12 : 6} caractères
                        </p>
                        {isEpvSubdomain && (
                            <>
                                <p
                                    className={
                                        validation.specialCharCheck
                                            ? "text-green-600"
                                            : "text-red-600"
                                    }
                                >
                                    • Un caractère spécial (ex : !@#$%)
                                </p>
                                <p
                                    className={
                                        validation.uppercaseCheck
                                            ? "text-green-600"
                                            : "text-red-600"
                                    }
                                >
                                    • Au moins une majuscule
                                </p>
                                <p
                                    className={
                                        validation.lowercaseCheck
                                            ? "text-green-600"
                                            : "text-red-600"
                                    }
                                >
                                    • Au moins une minuscule
                                </p>
                                <p
                                    className={
                                        validation.digitCheck
                                            ? "text-green-600"
                                            : "text-red-600"
                                    }
                                >
                                    • Au moins un chiffre
                                </p>
                            </>
                        )}
                    </div>

                    {error && (
                        <p className="text-left text-red-500 text-xs italic">
                            {error}
                        </p>
                    )}
                    <div className="flex justify-end">
                        <Button
                            action={register}
                            className={"p-8"}
                            name={"Demander l'activation du compte"}
                            loading={isSubmitting}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
